<script setup>
/* global axios */
// Aba "Ligações SIP (PABX)" das configurações da caixa — mesmo lugar e desenho do
// BCVOZ, mas para qualquer PABX IP pelo conector SIP do AstraCalls. As ligações
// passam a tocar no CallDock como as do WhatsApp (sessão = linha SIP da caixa).
// As senhas vão direto para o conector: nunca voltam para cá (só "já configurada").
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import NextButton from 'dashboard/components-next/button/Button.vue';
import SipField from './sipPabx/SipField.vue';
import SipStatusBadge from './sipPabx/SipStatusBadge.vue';
import SipAgentExtensions from './sipPabx/SipAgentExtensions.vue';

const props = defineProps({
  inbox: { type: Object, required: true },
});

const MODES = ['register_out', 'register_in'];
const TRANSPORTS = ['udp', 'tcp'];
const INPUT_CLASS =
  'w-full px-3 py-2 text-sm border rounded-lg border-n-weak bg-n-alpha-1 text-n-slate-12';

const { t } = useI18n();
const route = useRoute();
const store = useStore();
const base = `/api/v1/accounts/${route.params.accountId}/kanban/sip_pabx_configs`;

const connectors = ref([]);
const saving = ref(false);
const hasConfig = ref(false);
const status = ref(null);
const form = ref({
  enabled: true,
  whatsapp_provider_id: null,
  mode: 'register_out',
  sip_host: '',
  sip_port: 5060,
  transport: 'udp',
  proxy: '',
  sip_user: '',
  sip_auth_user: '',
  sip_password: '',
  did: '',
});
const agentRows = ref([]);

const accountAgents = computed(() => store.getters['agents/getAgents']);
const lineStatus = computed(() => status.value?.line || {});
const agentStatus = computed(() => status.value?.agents || {});
const connectorServer = computed(() => {
  const sip = status.value?.sip;
  return sip ? `${sip.host}:${sip.port}` : '';
});

const loadStatus = async () => {
  if (!hasConfig.value) return;
  try {
    const { data } = await axios.get(`${base}/status`, {
      params: { inbox_id: props.inbox.id },
    });
    status.value = data.data;
  } catch (e) {
    status.value = null;
  }
};

const load = async () => {
  const { data } = await axios.get(base, {
    params: { inbox_id: props.inbox.id },
  });
  connectors.value = data.data.connectors || [];
  const cfg = data.data.config;
  if (cfg) {
    hasConfig.value = true;
    form.value = { ...form.value, ...cfg, sip_password: '' };
    agentRows.value = cfg.agents.map(a => ({
      user_id: a.user_id,
      extension: a.extension,
      auth_user: a.auth_user || '',
      password: '',
      has_password: true,
    }));
  } else if (connectors.value.length) {
    form.value.whatsapp_provider_id = connectors.value[0].id;
  }
  loadStatus();
};

const save = async () => {
  saving.value = true;
  try {
    await axios.post(base, {
      ...form.value,
      inbox_id: props.inbox.id,
      agents: agentRows.value
        .filter(r => r.user_id && r.extension)
        .map(
          ({ user_id: userId, extension, auth_user: authUser, password }) => ({
            user_id: userId,
            extension,
            auth_user: authUser,
            password,
          })
        ),
    });
    hasConfig.value = true;
    form.value.sip_password = '';
    agentRows.value = agentRows.value.map(r => ({
      ...r,
      password: '',
      has_password: true,
    }));
    useAlert(t('INBOX_MGMT.SIP_PABX.SAVED'));
    loadStatus();
  } catch (e) {
    useAlert(e?.response?.data?.error || t('INBOX_MGMT.SIP_PABX.ERROR'));
  } finally {
    saving.value = false;
  }
};

onMounted(() => {
  store.dispatch('agents/get');
  load().catch(() => {});
});
</script>

<template>
  <div class="flex flex-col gap-6 p-6 mx-6">
    <div>
      <h4 class="text-base font-medium text-n-slate-12">
        {{ t('INBOX_MGMT.SIP_PABX.TITLE') }}
      </h4>
      <p class="mt-1 text-sm text-n-slate-11">
        {{ t('INBOX_MGMT.SIP_PABX.DESC') }}
      </p>
    </div>

    <p
      v-if="!connectors.length"
      class="p-3 text-sm rounded-lg bg-n-amber-3 text-n-amber-11"
    >
      {{ t('INBOX_MGMT.SIP_PABX.CONNECTOR_EMPTY') }}
    </p>

    <template v-else>
      <label
        class="flex items-center gap-2 text-sm font-medium text-n-slate-12"
      >
        <input
          v-model="form.enabled"
          type="checkbox"
          class="rounded border-n-weak"
        />
        {{ t('INBOX_MGMT.SIP_PABX.ENABLED_LABEL') }}
      </label>

      <SipField
        v-if="connectors.length > 1"
        :label="t('INBOX_MGMT.SIP_PABX.CONNECTOR_LABEL')"
      >
        <select v-model="form.whatsapp_provider_id" :class="INPUT_CLASS">
          <option v-for="c in connectors" :key="c.id" :value="c.id">
            {{ c.name }}
          </option>
        </select>
      </SipField>

      <section class="flex flex-col gap-3">
        <h5 class="text-sm font-semibold text-n-slate-12">
          {{ t('INBOX_MGMT.SIP_PABX.MODE_TITLE') }}
        </h5>
        <div class="flex gap-2">
          <NextButton
            v-for="mode in MODES"
            :key="mode"
            :label="
              t(
                mode === 'register_out'
                  ? 'INBOX_MGMT.SIP_PABX.MODE_REGISTER_OUT'
                  : 'INBOX_MGMT.SIP_PABX.MODE_REGISTER_IN'
              )
            "
            sm
            :faded="form.mode !== mode"
            :blue="form.mode === mode"
            :slate="form.mode !== mode"
            @click="form.mode = mode"
          />
        </div>
        <p class="text-xs text-n-slate-10">
          {{
            form.mode === 'register_out'
              ? t('INBOX_MGMT.SIP_PABX.MODE_REGISTER_OUT_HINT')
              : t('INBOX_MGMT.SIP_PABX.MODE_REGISTER_IN_HINT')
          }}
        </p>
        <p
          v-if="form.mode === 'register_in' && connectorServer"
          class="text-sm text-n-slate-12"
        >
          {{ t('INBOX_MGMT.SIP_PABX.CONNECTOR_SERVER') }}:
          <code class="px-1 rounded bg-n-alpha-2">{{ connectorServer }}</code>
        </p>
      </section>

      <section class="flex flex-col gap-3">
        <h5 class="text-sm font-semibold text-n-slate-12">
          {{ t('INBOX_MGMT.SIP_PABX.SERVER_TITLE') }}
        </h5>
        <div class="grid grid-cols-[1fr_6rem_6rem] gap-3">
          <SipField :label="t('INBOX_MGMT.SIP_PABX.HOST_LABEL')">
            <input
              v-model="form.sip_host"
              type="text"
              :placeholder="t('INBOX_MGMT.SIP_PABX.HOST_PLACEHOLDER')"
              :class="INPUT_CLASS"
            />
          </SipField>
          <SipField :label="t('INBOX_MGMT.SIP_PABX.PORT_LABEL')">
            <input
              v-model.number="form.sip_port"
              type="number"
              :class="INPUT_CLASS"
            />
          </SipField>
          <SipField :label="t('INBOX_MGMT.SIP_PABX.TRANSPORT_LABEL')">
            <select v-model="form.transport" :class="INPUT_CLASS">
              <option v-for="tr in TRANSPORTS" :key="tr" :value="tr">
                {{ tr.toUpperCase() }}
              </option>
            </select>
          </SipField>
        </div>
        <SipField :label="t('INBOX_MGMT.SIP_PABX.PROXY_LABEL')">
          <input
            v-model="form.proxy"
            type="text"
            :placeholder="t('INBOX_MGMT.SIP_PABX.PROXY_PLACEHOLDER')"
            :class="INPUT_CLASS"
          />
        </SipField>
        <div class="grid grid-cols-3 gap-3">
          <SipField :label="t('INBOX_MGMT.SIP_PABX.LINE_USER_LABEL')">
            <input v-model="form.sip_user" type="text" :class="INPUT_CLASS" />
          </SipField>
          <SipField
            :label="t('INBOX_MGMT.SIP_PABX.AUTH_USER_LABEL')"
            :hint="t('INBOX_MGMT.SIP_PABX.AUTH_USER_HINT')"
          >
            <input
              v-model="form.sip_auth_user"
              type="text"
              autocomplete="off"
              :class="INPUT_CLASS"
            />
          </SipField>
          <SipField :label="t('INBOX_MGMT.SIP_PABX.LINE_PASSWORD_LABEL')">
            <input
              v-model="form.sip_password"
              type="password"
              autocomplete="new-password"
              :placeholder="
                hasConfig ? t('INBOX_MGMT.SIP_PABX.PASSWORD_SET') : ''
              "
              :class="INPUT_CLASS"
            />
          </SipField>
        </div>
        <SipField
          :label="t('INBOX_MGMT.SIP_PABX.DID_LABEL')"
          :hint="t('INBOX_MGMT.SIP_PABX.DID_HINT')"
        >
          <input v-model="form.did" type="text" :class="INPUT_CLASS" />
        </SipField>
      </section>

      <section class="flex flex-col gap-3">
        <div>
          <h5 class="text-sm font-semibold text-n-slate-12">
            {{ t('INBOX_MGMT.SIP_PABX.AGENTS_TITLE') }}
          </h5>
          <p class="mt-1 text-xs text-n-slate-10">
            {{ t('INBOX_MGMT.SIP_PABX.AGENTS_DESC') }}
          </p>
        </div>
        <SipAgentExtensions
          v-model="agentRows"
          :agents="accountAgents"
          :status="agentStatus"
        />
      </section>

      <section v-if="hasConfig" class="flex items-center gap-3">
        <span class="text-sm font-medium text-n-slate-12">
          {{ t('INBOX_MGMT.SIP_PABX.STATUS_LINE') }}
        </span>
        <SipStatusBadge :state="lineStatus.state" :error="lineStatus.error" />
        <NextButton
          :label="t('INBOX_MGMT.SIP_PABX.STATUS_REFRESH')"
          icon="i-lucide-refresh-cw"
          slate
          ghost
          sm
          @click="loadStatus"
        />
      </section>

      <div>
        <NextButton
          :label="t('INBOX_MGMT.SIP_PABX.SAVE')"
          blue
          sm
          :is-loading="saving"
          @click="save"
        />
      </div>
    </template>
  </div>
</template>
